import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { useAuth } from "../auth/AuthProvider";
import { LocalLibraryStore } from "./localStore";
import { createSupabaseRemote } from "./remote";
import { syncLibrary } from "./sync";

type SyncStatus = "off" | "idle" | "syncing" | "error";

interface LibraryContextValue {
  store: LocalLibraryStore;
  syncStatus: SyncStatus;
  syncNow(): void;
}

const LibraryContext = createContext<LibraryContextValue | null>(null);

const SYNC_INTERVAL_MS = 60_000;
const SYNC_DEBOUNCE_MS = 2_000;

function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Biblioteca local siempre disponible. Con sesion iniciada se sincroniza con la nube al entrar,
 * tras cada cambio (con debounce), al volver a la pestana y cada minuto.
 */
export function LibraryProvider({ children }: { children: ReactNode }) {
  const [store] = useState(() => new LocalLibraryStore(safeLocalStorage()));
  const { state, client } = useAuth();
  const [syncStatus, setSyncStatus] = useState<SyncStatus>("off");
  const running = useRef(false);

  const remote = useMemo(() => (client && state.status === "signedIn" ? createSupabaseRemote(client) : null), [client, state.status]);

  const syncNow = useCallback(() => {
    if (!remote || running.current) return;
    running.current = true;
    setSyncStatus("syncing");
    syncLibrary(store, remote)
      .then(() => setSyncStatus("idle"))
      .catch(() => setSyncStatus("error"))
      .finally(() => {
        running.current = false;
      });
  }, [remote, store]);

  useEffect(() => {
    if (!remote) {
      setSyncStatus("off");
      return;
    }
    syncNow();
    const interval = window.setInterval(syncNow, SYNC_INTERVAL_MS);
    const onVisible = () => document.visibilityState === "visible" && syncNow();
    document.addEventListener("visibilitychange", onVisible);
    let debounce: number | undefined;
    const unsubscribe = store.subscribe(() => {
      if (running.current) return;
      window.clearTimeout(debounce);
      debounce = window.setTimeout(syncNow, SYNC_DEBOUNCE_MS);
    });
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(debounce);
      document.removeEventListener("visibilitychange", onVisible);
      unsubscribe();
    };
  }, [remote, store, syncNow]);

  const value = useMemo(() => ({ store, syncStatus, syncNow }), [store, syncStatus, syncNow]);
  return <LibraryContext.Provider value={value}>{children}</LibraryContext.Provider>;
}

export function useLibrary(): LibraryContextValue {
  const ctx = useContext(LibraryContext);
  if (!ctx) throw new Error("useLibrary fuera de LibraryProvider");
  return ctx;
}

/** Se re-renderiza con cada cambio de la biblioteca local. */
export function useLibrarySnapshot() {
  const { store } = useLibrary();
  useSyncExternalStore(store.subscribe, store.getSnapshot);
  return store;
}
